import { useEffect, useRef } from 'react';
import { captureChatException } from 'shared/sentry';
import { subscribeForegroundPush } from '../../../../../shared/pusNotification/fcm/pushEvents';
import { parseTipJson } from '../../../../../shared/utils/chatUtil';
import {
  getRoomMessageBody,
  JUICYHOUSE_CALL_EVENT,
  isReplacementEvent,
  shouldRefreshRoomsForTimelineEvent,
} from '../../../../../shared/utils/matrix/matrixRoomUtils';

const ROOM_INFO_REFRESH_PUSH_EVENTS = new Set([
  'message_content_sold',
  'message_bundle_sold',
  'tip_received',
  'payment_tip',
  'tip',
  'message_tip',
  'creator_sales_tips',
]);

const resolvePushRefreshEventTypes = (detail = {}) => {
  const rawData = detail?.data || {};
  const candidates = [
    detail?.eventType,
    rawData?.eventType,
    rawData?.notificationType,
    rawData?.type,
    detail?.type,
  ];
  const matched = [];
  for (let i = 0; i < candidates.length; i += 1) {
    const value = candidates[i];
    if (value == null || !String(value).trim()) continue;
    const normalized = String(value).trim();
    if (ROOM_INFO_REFRESH_PUSH_EVENTS.has(normalized)) {
      matched.push(normalized);
    }
  }
  return matched;
};

const resolvePushSubscriberUserId = (detail = {}) => {
  const rawData = detail?.data || {};
  let meta = rawData?.meta;
  if (typeof meta === 'string') {
    try {
      meta = JSON.parse(meta);
    } catch {
      meta = null;
    }
  }
  const candidates = [
    rawData?.from_user_id,
    rawData?.fromUserId,
    rawData?.user_id,
    rawData?.userId,
    rawData?.subscriber_id,
    rawData?.subscriberId,
    rawData?.buyer_id,
    rawData?.buyerId,
    meta?.from_user_id,
    meta?.user_id,
    meta?.userId,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const value = candidates[i];
    if (value != null && String(value).trim()) return String(value);
  }
  return null;
};

const isCallTimelineEvent = (event) => {
  const type = String(event?.getType?.() || '');
  return type.startsWith('m.call.') || type === JUICYHOUSE_CALL_EVENT;
};

const TIP_SPEND_FOLLOW_UP_REFRESH_MS = 4500;
const LEGACY_TIP_LINE_REGEX = /\+\$[\d.]+\s+tipped/i;

const isTipTimelineMessage = (event) => {
  const body = getRoomMessageBody(event);
  if (!body) return false;
  return Boolean(parseTipJson(body) || LEGACY_TIP_LINE_REGEX.test(body));
};

/**
 * Keeps creator-facing Matrix room metadata fresh while a 1:1 chat is open:
 * PPV badges, subscriber spend totals / recent spend in the header, etc.
 * Refetches room info only after message-related timeline activity (send, edit,
 * redaction, decrypt) — not on receipts, typing, or other sync noise.
 */
export function useCreatorMessagingPurchasePolling({
  enabled,
  userId,
  isRoomInfoReady,
  refreshRoomInfo,
  client,
  roomId,
  timelineDebounceMs = 1200,
}) {
  const refreshRef = useRef(refreshRoomInfo);
  refreshRef.current = refreshRoomInfo;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const refreshRoomInfoInBackground = () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    try {
      refreshRef.current?.({ force: true, background: true });
    } catch (e) {
      captureChatException(e, {
        tags: { feature: 'matrix-chat', action: 'room-info-refresh' },
      });
    }
  };

  useEffect(() => {
    if (!enabled || !userId || !refreshRoomInfo) return undefined;

    const onPush = (event) => {
      const matchedEventTypes = resolvePushRefreshEventTypes(event?.detail);
      if (!matchedEventTypes.length) return;

      const subscriberId = resolvePushSubscriberUserId(event?.detail);
      if (subscriberId && String(subscriberId) !== String(userIdRef.current)) return;

      refreshRoomInfoInBackground();
    };

    return subscribeForegroundPush(onPush);
  }, [enabled, userId, refreshRoomInfo]);

  useEffect(() => {
    if (!enabled || !client || !roomId || !refreshRoomInfo) return undefined;

    let debounceId = null;
    let tipFollowUpId = null;

    const scheduleRefresh = () => {
      if (debounceId != null) window.clearTimeout(debounceId);
      debounceId = window.setTimeout(() => {
        debounceId = null;
        refreshRoomInfoInBackground();
      }, timelineDebounceMs);
    };

    const scheduleTipSpendFollowUpRefresh = () => {
      if (tipFollowUpId != null) window.clearTimeout(tipFollowUpId);
      tipFollowUpId = window.setTimeout(() => {
        tipFollowUpId = null;
        refreshRoomInfoInBackground();
      }, TIP_SPEND_FOLLOW_UP_REFRESH_MS);
    };

    const onTimeline = (event, room, toStartOfTimeline) => {
      try {
        if (toStartOfTimeline) return;
        if (!room || room.roomId !== roomId) return;
        if (isCallTimelineEvent(event)) return;
        if (!shouldRefreshRoomsForTimelineEvent(event)) return;

        // Message edits (e.g. lock_content after purchase) update Sold badges immediately.
        if (isReplacementEvent(event)) {
          refreshRoomInfoInBackground();
          return;
        }

        if (isTipTimelineMessage(event)) {
          scheduleRefresh();
          scheduleTipSpendFollowUpRefresh();
          return;
        }

        scheduleRefresh();
      } catch (e) {
        captureChatException(e, {
          tags: { feature: 'matrix-chat', matrix: 'timeline-room-info' },
        });
      }
    };

    client.on?.('Room.timeline', onTimeline);

    return () => {
      client.off?.('Room.timeline', onTimeline);
      if (debounceId != null) window.clearTimeout(debounceId);
      if (tipFollowUpId != null) window.clearTimeout(tipFollowUpId);
    };
  }, [enabled, client, roomId, refreshRoomInfo, timelineDebounceMs]);
}
